// frontend/user/src/components/batch-action-bar.tsx
// .pen BLWNx HSNvP(页面-文件-多选)/ UGuBc ldHRV(文件夹详情-多选)batchToolbar:
//   下载 / 分享 / 移动 / 删除(红)。固定底部,替换 TabBar。
//
// 语义:
//   下载/分享 — 选中含文件夹时 disabled(download/pack 均不支持文件夹,与单文件 ActionSheet 一致)
//   下载 — 顺序逐个取预签名 URL 触发 <a> 下载(浏览器可能拦多文件下载,首个之外需用户允许)
//   分享 — 跳 /pack/create?file_ids=a,b,c(pack-create 预选,任务 11)
//   移动 — POST /gapi/user/files/move-batch(MoveFileDialog mode="move" 多文件)
//   删除 — mdYih 确认后顺序循环 POST /gapi/user/files/{id}/trash(后端无批量端点)

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, useToast } from '@dogdisk/shared'
import { DeleteConfirmDialog } from './modals/delete-confirm-dialog'
import { MoveFileDialog } from './move-file-dialog'
import type { FileRow } from '../_lib/offline-cache'
import { resourceUrl } from '../_lib/resource-node-pref'

type Props = {
  files: FileRow[]
  onDone: () => void
}

export function BatchActionBar({ files, onDone }: Props) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)

  const empty = files.length === 0
  const hasFolder = files.some((f) => f.type === 'folder')

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['file', 'list'] })
  }

  const trashMut = useMutation({
    mutationFn: async () => {
      // snapshot:循环执行期间选中集可能被用户继续改动
      const snapshot = [...files]
      for (const f of snapshot) {
        await apiClient.post(`/gapi/user/files/${f.id}/trash`, {})
      }
    },
    onSuccess: () => {
      invalidate()
      setConfirmDelete(false)
      onDone()
    },
    onError: (e) => {
      toast.push(e instanceof Error ? e.message : '删除失败', { variant: 'error' })
      invalidate()
    },
  })

  const downloadMut = useMutation({
    mutationFn: async () => {
      // snapshot:循环执行期间选中集可能被用户继续改动
      const snapshot = [...files]
      for (const f of snapshot) {
        const resp = await apiClient.get<{ url: string }>(`/gapi/user/download/${f.id}`)
        if (!resp.url) continue
        const a = document.createElement('a')
        a.href = resourceUrl(resp.url)
        document.body.appendChild(a)
        a.click()
        a.remove()
        // 浏览器逐个触发下载的最小间隔
        await new Promise((r) => setTimeout(r, 400))
      }
    },
    onSuccess: () => onDone(),
    onError: (e) => toast.push(e instanceof Error ? e.message : '下载失败', { variant: 'error' }),
  })

  const onShare = () => {
    navigate(`/pack/create?file_ids=${files.map((f) => f.id).join(',')}`)
  }

  const BTN = 'flex flex-col items-center gap-1.5 px-2 disabled:opacity-40'
  const LABEL = 'text-[13px] font-medium'

  return (
    <>
      <div
        data-testid="batch-action-bar"
        className="fixed inset-x-0 bottom-0 z-30 mx-auto flex w-full max-w-[402px] justify-around border-t border-[#F3F4F6] bg-white pt-3.5 shadow-[0_-2px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'max(30px, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          data-testid="batch-download-btn"
          className={BTN}
          disabled={empty || hasFolder || downloadMut.isPending}
          onClick={() => downloadMut.mutate()}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1A1A1A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" x2="12" y1="15" y2="3" />
          </svg>
          <span className={`${LABEL} text-[#1A1A1A]`}>{downloadMut.isPending ? '下载中…' : '下载'}</span>
        </button>
        <button
          type="button"
          data-testid="batch-share-btn"
          className={BTN}
          disabled={empty || hasFolder}
          onClick={onShare}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1A1A1A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
            <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
          </svg>
          <span className={`${LABEL} text-[#1A1A1A]`}>分享</span>
        </button>
        <button
          type="button"
          data-testid="batch-move-btn"
          className={BTN}
          disabled={empty}
          onClick={() => setMoveOpen(true)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1A1A1A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2" />
            <path d="M2 13h10" />
            <path d="m9 16 3-3-3-3" />
          </svg>
          <span className={`${LABEL} text-[#1A1A1A]`}>移动</span>
        </button>
        <button
          type="button"
          data-testid="batch-delete-btn"
          className={BTN}
          disabled={empty || trashMut.isPending}
          onClick={() => setConfirmDelete(true)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FF6B6B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" x2="10" y1="11" y2="17" />
            <line x1="14" x2="14" y1="11" y2="17" />
          </svg>
          <span className={`${LABEL} text-[#FF6B6B]`}>{trashMut.isPending ? '删除中…' : '删除'}</span>
        </button>
      </div>

      {moveOpen && (
        <MoveFileDialog
          files={files}
          mode="move"
          onClose={() => setMoveOpen(false)}
          onDone={() => {
            setMoveOpen(false)
            onDone()
          }}
        />
      )}

      <DeleteConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => trashMut.mutate()}
        title={`删除 ${files.length} 项?`}
        subtitle="所选项目将移入回收站,30 天后自动清除。"
        confirmText="删除"
        loading={trashMut.isPending}
      />
    </>
  )
}
